export enum MetricEventName {
    // File upload
    FILE_SELECTED = 'FILE_SELECTED',
    INVALID_FILE_SELECTED = 'INVALID_FILE_SELECTED',
    FILE_UPLOAD_START = 'FILE_UPLOAD_START',
    FILE_UPLOAD_COMPLETE = 'FILE_UPLOAD_COMPLETE',
    FILE_UPLOAD_ERROR = 'FILE_UPLOAD_ERROR',
    // Validation & redirect
    VALIDATION_BANNER_SHOWN = 'VALIDATION_BANNER_SHOWN',
    VALIDATION_BANNER_DISMISSED = 'VALIDATION_BANNER_DISMISSED',
    SUGGESTION_LINK_CLICKED = 'SUGGESTION_LINK_CLICKED',
    SUGGESTION_LOADED = 'SUGGESTION_LOADED',
    // File hand-off: the user's file travelling to a suggested tool (T1.2/T1.7).
    // STARTED fires on the origin when the tray is stashed, COMPLETED on the destination
    // when the file is actually staged — the gap between them is where users were lost.
    FILE_HANDOFF_STARTED = 'FILE_HANDOFF_STARTED',
    FILE_HANDOFF_COMPLETED = 'FILE_HANDOFF_COMPLETED',
    FILE_HANDOFF_MISSED = 'FILE_HANDOFF_MISSED',
    /**
     * A file-card preview thumbnail could not be fetched, after an automatic retry.
     *
     * Added because there was no signal at all: a customer reported "the images fail to load"
     * on /convert-png-to-webp and there was nothing in frontend-events to look at. Carries the
     * edge host, because thumbnails come from whichever edge served the upload and a network
     * that blocks those subdomains is the likeliest cause we cannot see from here.
     */
    THUMBNAIL_LOAD_FAILED = 'THUMBNAIL_LOAD_FAILED',
    /** A continuation offered on the result screen was taken (T3.4). */
    NEXT_TOOL_CLICKED = 'NEXT_TOOL_CLICKED',
    /**
     * The outcome of that continuation: did the file actually travel with them (T3.4 phase 2)?
     * CLICKED minus CHAINED-with-carried is the population still re-picking their own file, which
     * is the number that says whether this worked.
     */
    NEXT_TOOL_CHAINED = 'NEXT_TOOL_CHAINED',
    /**
     * Re-edit taken from the tool page rather than from /downloads (T3.3). Measuring this
     * against Re-edit on /downloads is how we learn whether the detour was costing re-runs.
     */
    RECENT_REEDIT_CLICKED = 'RECENT_REEDIT_CLICKED',
    /**
     * A file was re-used from the session list instead of being picked off the device again
     * (T6.2). This is the number that decides whether the tray is worth promoting to a
     * server-backed library (T6.5) or leaving as a browser-local convenience.
     */
    SESSION_FILE_PICKED = 'SESSION_FILE_PICKED',
    /** A session file this page could not take was carried to one that could (T6.4). */
    SESSION_FILE_ROUTED = 'SESSION_FILE_ROUTED',
    // Conversion
    CONVERT_START = 'CONVERT_START',
    CONVERT_COMPLETE = 'CONVERT_COMPLETE',
    CONVERT_ERROR = 'CONVERT_ERROR',
    FILE_DOWNLOAD = 'FILE_DOWNLOAD',
    // Auth & errors
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',
    LOGIN_ERROR = 'LOGIN_ERROR',
    API_ERROR = 'API_ERROR',
    WEBSOCKET_DISCONNECT = 'WEBSOCKET_DISCONNECT',
    // Navigation
    PAGE_VIEW = 'PAGE_VIEW',
    FEATURE_SEARCH = 'FEATURE_SEARCH',
    FEATURE_SEARCH_CLICK = 'FEATURE_SEARCH_CLICK',
    // Funnel spine (improvement-sweep P2 logs-to-add): the modal preview was
    // server-flag-only, downloads had no session context, and option usage and
    // upload abandonment were guesses.
    /** Preview modal opened from a result card. {workflowId, kind} */
    PREVIEW_OPENED = 'PREVIEW_OPENED',
    /**
     * Page hidden while files were staged but never converted — the measured
     * form of the ~23% upload→convert loss. {page, fileCount}
     */
    UPLOAD_ABANDONED = 'UPLOAD_ABANDONED',
    /** A convert-option knob changed (sampled 1:10 client-side). {key, page} */
    OPTION_CHANGED = 'OPTION_CHANGED',
    // Help-us-grow initiative (2026-08): the link-encouragement surfaces on
    // /downloads and /link-us. Success = credit copies and link-us copies,
    // measured against pitch impressions.
    /** The grow pitch box rendered above the file list. {page} */
    GROW_PITCH_SHOWN = 'GROW_PITCH_SHOWN',
    /** The grow pitch box was dismissed (hidden for 7 days). */
    GROW_PITCH_DISMISSED = 'GROW_PITCH_DISMISSED',
    /** The pitch box CTA to /link-us was clicked. */
    GROW_PITCH_CTA_CLICKED = 'GROW_PITCH_CTA_CLICKED',
    /** Any share-row button on a result card. {channel, tool} */
    SHARE_CLICKED = 'SHARE_CLICKED',
    /** The "Add us to your site" credit panel was opened. {tool} */
    CREDIT_PANEL_OPENED = 'CREDIT_PANEL_OPENED',
    /** A credit snippet was copied. {format, tool} */
    CREDIT_COPIED = 'CREDIT_COPIED',
    /** A snippet was copied on /link-us. {kind, tool} */
    LINKUS_SNIPPET_COPIED = 'LINKUS_SNIPPET_COPIED',
}
